import {
  Box,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import React from "react";
import type { PageOption } from "@/roles/protection/pageOptions";

interface HierarchicalCheckboxProps {
  options: PageOption[];
  selectedKeys: string[];
  onChange: (keys: string[]) => void;
}

const HierarchicalCheckbox: React.FC<HierarchicalCheckboxProps> = ({
  options,
  selectedKeys,
  onChange,
}) => {
  const isSelected = (key: string) => selectedKeys.includes(key);

  const toggleFlat = (key: string) => {
    onChange(
      isSelected(key)
        ? selectedKeys.filter((k) => k !== key)
        : [...selectedKeys, key]
    );
  };

  const toggleParent = (option: PageOption, checked: boolean) => {
    if (!option.children) {
      toggleFlat(option.key);
      return;
    }
    const childKeys = option.children.map((c) => c.key);
    if (checked) {
      const next = new Set(selectedKeys);
      for (const k of childKeys) next.add(k);
      next.delete(option.key);
      onChange(Array.from(next));
    } else {
      const toRemove = new Set([option.key, ...childKeys]);
      onChange(selectedKeys.filter((k) => !toRemove.has(k)));
    }
  };

  const toggleChild = (parentOption: PageOption, childKey: string) => {
    if (!parentOption.children) return;
    const childKeys = parentOption.children.map((c) => c.key);
    let next: string[];

    if (isSelected(parentOption.key)) {
      // Parent key is present — expand to individual children minus the toggled one
      const keep = new Set(childKeys);
      keep.delete(childKey);
      next = selectedKeys.filter((k) => k !== parentOption.key && !childKeys.includes(k));
      for (const k of keep) next.push(k);
    } else if (isSelected(childKey)) {
      next = selectedKeys.filter((k) => k !== childKey);
    } else {
      next = [...selectedKeys, childKey];
    }

    onChange(next);
  };

  const getParentState = (option: PageOption) => {
    if (!option.children) return { checked: isSelected(option.key), indeterminate: false };
    if (isSelected(option.key)) return { checked: true, indeterminate: false };
    const childKeys = option.children.map((c) => c.key);
    const selectedCount = childKeys.filter((k) => isSelected(k)).length;
    return {
      checked: selectedCount === childKeys.length,
      indeterminate: selectedCount > 0 && selectedCount < childKeys.length,
    };
  };

  const isChildSelected = (parentOption: PageOption, childKey: string) => {
    if (isSelected(parentOption.key)) return true;
    return isSelected(childKey);
  };

  return (
    <Box>
      {options.map((option) => {
        const state = getParentState(option);
        return (
          <Box key={option.key}>
            <FormControlLabel
              label={option.label}
              control={
                <Checkbox
                  checked={state.checked}
                  indeterminate={state.indeterminate}
                  onChange={(_, checked) => toggleParent(option, checked)}
                  sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                />
              }
            />
            {option.children && (
              <Box sx={{ display: "flex", flexDirection: "column", ml: 3 }}>
                {option.children.map((child) => (
                  <FormControlLabel
                    key={child.key}
                    label={child.label}
                    control={
                      <Checkbox
                        checked={isChildSelected(option, child.key)}
                        onChange={() => toggleChild(option, child.key)}
                        sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                      />
                    }
                  />
                ))}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default HierarchicalCheckbox;
